import { BubbleForm, BubbleOverlay } from '@go-client/components/BubbleUI';
import { BubbleFormPage } from '@go-client/components/BubbleUI/BubbleForm';
import { RESERVABLE_EXPERIENCE_TYPE } from '@go-shared/types/reservable';
import { BareReserveSchedule, ReserveSchedule } from '@go-shared/types/reserveSchedule';
import React, { useState } from 'react';
import { useEffect } from 'react';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import buildCreateReservableScheduleWorkflow from './Workflows/CreateReservableScheduleWorkflow';
import { BareEventTemplate } from '@go-shared/types/eventTemplate';
import {
  buildFormik as buildScheduleFormik,
  GENERATE_INITIAL_VALUES,
  ReservableScheduleFormikForm,
  ReservableScheduleFormikProps,
} from '../Formik/ReservableScheduleFormik';
import {
  buildFormik as buildEventFormik,
  eventFormikValuesToNewEvent,
} from '../Formik/EventTemplateFormik';
import buildEditReservableScheduleWorkflow from './Workflows/EditReservableScheduleWorkflow';
import { User } from '@go-shared/types/user';
import { getUsers } from '@go-client/lib/api/user';
import { FormikProps } from 'formik';
import { BareVenueTemplate } from '@go-shared/types/venueTemplate';
import { getVenueTemplatesByPropertyId } from '@go-client/lib/api/venueTemplate';
import { EventTemplateFormikForm } from '../Formik/EventTemplateFormik';
import { ObjectId } from 'bson';
import { findScheduleByEvent } from '@go-client/lib/api/reservable';
import { bare } from '@go-shared/lib/reserve';
import { canManageScheduleSomeRule, canModifyMultiPropertyItem } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { useBubbleDrawerTools } from '../BubbleUI/BubbleDrawer';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { RemapReservationSlot } from '@go-shared/types/reservation';

export interface ScheduleWorkflowPreqs {
  property: string;
  mode: ReservableScheduleFormMode;
  scheduleFormik: FormikProps<ReservableScheduleFormikForm>;
  eventFormik: FormikProps<EventTemplateFormikForm>;
  experienceType: RESERVABLE_EXPERIENCE_TYPE;
  expTypeLabel: string;
  libraryLocked: boolean;
  scheduleLocked: boolean;
  isBusy: boolean;
  setIsBusy: (isBusy: boolean) => void;
  remaps: RemapReservationSlot[];
  setRemaps: (newMaps: RemapReservationSlot[]) => void;
  onSubmitSchedule?: () => Promise<void>;
  onSave?: () => Promise<void>;
  onClose: () => void;
}

export interface ResRecPathDataBundle {
  users: User[];
  schedule?: BareReserveSchedule;
  venueTemplates: BareVenueTemplate[];
}

export interface ReservableScheduleFormMode {
  type: ReservableScheduleFormikProps['type'];
  eventTemplate?: BareEventTemplate;
}

export interface ReservableSchedulePathProps {
  mode: ReservableScheduleFormMode;
  users?: User[];
  venueTemplates?: BareVenueTemplate[];
  onClose: () => void;
  onSubmitSchedule?: () => Promise<void>;
  onSave?: () => Promise<void>;
}

export type RESERVABLE_SCHEDULE_BREADCRUMBS =
  | 'event'
  | 'experienceType'
  | 'tickets'
  | 'packages'
  | 'addons'
  | 'schedule'
  | 'communications'
  | 'slotMapping';

export default function ReservableSchedulePath(props: ReservableSchedulePathProps) {
  const { activeProperty } = useActiveProperty();
  const { user } = useUserInfo();
  const { setVerifyClose } = useBubbleDrawerTools();
  const serverInfo = useServerInfo();

  const [dataBundle, setDataBundle] = useState<ResRecPathDataBundle | undefined>(undefined);
  const [isBusy, setIsBusy] = useState(false);
  const [remaps, setRemaps] = useState<RemapReservationSlot[]>([]);

  const eventFormik = buildEventFormik(activeProperty, props.mode.eventTemplate, async values => {
    let newEventTmpl = eventFormikValuesToNewEvent(
      values,
      activeProperty,
      props.mode.eventTemplate
    );
    return newEventTmpl;
  });

  const { experienceType } = eventFormik.values.reservables;

  const scheduleFormik = buildScheduleFormik(
    activeProperty._id,
    props.mode.eventTemplate?._id ?? new ObjectId().toHexString(),
    experienceType,
    undefined,
    async values => {
      const schedule: BareReserveSchedule = {
        _id: values._id,
        property: activeProperty._id,
        eventTemplate: values.template,
        rules: values.rules.map(r => ({
          _id: r._id,
          ruleFragments: r.ruleFragments,
          host: r.host,
          slots: r.slots,
          resources: r.resources,
          communications: r.communications,
          hidden: r.hidden,
        })),
      };

      return schedule;
    }
  );

  useEffect(() => {
    initialize().catch(console.error);
  }, []);

  useEffect(() => {
    setVerifyClose(eventFormik.dirty || scheduleFormik.dirty);
  }, [eventFormik.dirty, scheduleFormik.dirty]);

  useEffect(() => {
    if (dataBundle?.schedule) {
      const newValues = GENERATE_INITIAL_VALUES(
        activeProperty._id,
        props.mode.eventTemplate?._id ?? new ObjectId().toHexString(),
        eventFormik.values.reservables.experienceType,
        dataBundle.schedule
      );
      scheduleFormik.resetForm({ values: newValues });
    }
  }, [dataBundle?.schedule]);

  async function initialize() {
    const [users, venueTemplates] = await Promise.all([
      props.users ? Promise.resolve(props.users) : getUsers(activeProperty._id),
      props.venueTemplates
        ? Promise.resolve(props.venueTemplates)
        : getVenueTemplatesByPropertyId(activeProperty._id),
    ]);

    let schedule: ReserveSchedule | undefined;

    try {
      schedule = await (!props.mode.eventTemplate
        ? Promise.resolve(undefined)
        : findScheduleByEvent(activeProperty._id, props.mode.eventTemplate._id));
    } catch (err) {
      console.error(`No existing schedule found for ${props.mode.eventTemplate?._id}`);
    }

    setDataBundle({ schedule: schedule ? bare(schedule) : undefined, users, venueTemplates });
  }

  let startAtPage: number | undefined;
  const pages: Array<BubbleFormPage<ResRecPathDataBundle>> = [];

  const preqs: ScheduleWorkflowPreqs = {
    property: activeProperty._id,
    mode: props.mode,
    isBusy,
    setIsBusy,
    remaps,
    setRemaps,
    scheduleFormik,
    eventFormik,
    experienceType,
    expTypeLabel: experienceType === 'resource' ? 'Rental' : 'Ticket',
    libraryLocked: !canModifyMultiPropertyItem(
      user,
      serverInfo,
      activeProperty._id,
      props.mode.eventTemplate
    ),
    scheduleLocked: !canManageScheduleSomeRule(user, dataBundle?.schedule),
    onClose: props.onClose,
    onSubmitSchedule: props.onSubmitSchedule,
    onSave: props.onSave,
  };

  switch (props.mode.type) {
    case 'create':
      buildCreateReservableScheduleWorkflow(pages, preqs);
      startAtPage = 0;
      break;
    case 'edit':
      buildEditReservableScheduleWorkflow(pages, preqs);
      startAtPage = 5;
      break;
  }

  return (
    <div className="reservable-schedule-form">
      <BubbleOverlay loading={!dataBundle} hideBg loadingMinHeight="large">
        {dataBundle && (
          <BubbleForm pages={pages} startAtPage={startAtPage} resources={dataBundle} />
        )}
      </BubbleOverlay>
    </div>
  );
}
